import React, { useCallback, useEffect, useRef, useState } from 'react';
import { parsePhoneNumber } from 'awesome-phonenumber';
import type { PromptLoginWidgetData } from '../../types/widgets';
import { useOptionalConciergeContext } from '../../../ConciergeContextProvider';
import { isBackRoom } from '../../../../utils/domain';
import { applyPhoneMask, isPhoneValid, toDigits } from './promptLoginUtils';

/**
 * D-48 — `PromptLoginWidget`
 *
 * The voice-call surface's phone+OTP login card. Mirrors the text-side
 * `PromptLoginPart` (Cognito sign-in via `inlineSignIn` / `inlineVerifyOtp`
 * from `useConciergeContext`) but lives inline in the top-half stage —
 * no modal overlay — so the call shell stays visible the whole time.
 *
 * Behavior:
 *  - User types digits + Continue → exactly like text mode. The user
 *    clicking Continue is itself the confirmation; Cognito fires
 *    immediately.
 *  - User SPEAKS digits → the realtime model calls
 *    `submit_phone_for_login({phone, confirmed: false})` /
 *    `submit_otp_for_login({otp, confirmed: false})` which re-emits this
 *    widget with `data.prefill.{phone,otp}` set; we sync the input field
 *    and show a "Heard you say… is that right?" caption — but DO NOT
 *    fire Cognito yet. The model is expected to verbally read the
 *    digits back, wait for the user to say yes, and then call the tool
 *    AGAIN with `confirmed: true` — only then does the widget actually
 *    fire `inlineSignIn` / `inlineVerifyOtp`. This prevents the
 *    speech-mishearing footgun the live call `36fc1949-...` exposed:
 *    the model heard "8685" as "8665" and the old auto-submit timer
 *    sent the SMS to the wrong number before the user could correct.
 *  - On successful OTP verification, fire `onLoginCompleted` so the
 *    session hook can POST `/voice/auth-promote`, clear the widget, and
 *    nudge the model to retry the originally-blocked tool.
 *
 * Styled with `var(--voice-*)` tokens so brand-configured colors flow in
 * the same way other voice widgets get themed.
 */
export interface PromptLoginWidgetProps {
  data: PromptLoginWidgetData;
  /** Fired once Cognito verification succeeds and `inlineVerifyOtp` resolves OK. */
  onLoginCompleted?: () => void;
  /**
   * Fired when `inlineVerifyOtp` resolves with `{success: false}`. Lets
   * the session hook send a `response.create` to break the model's
   * silence and prompt the user for a re-read; widget stays on the OTP
   * step locally so the user can correct without retracting the flow.
   */
  onLoginFailed?: (reason: string) => void;
  /**
   * Plan 7b — when true, the widget renders the captured phone / OTP
   * digits but skips the prefill-driven Cognito effects and hides the
   * Submit + Resend buttons. Used by `VoiceWidgetInTextChat` to render a
   * persisted voice-mode login card inside the text chat replay.
   */
  readOnly?: boolean;
}

type LoginStep = 'phone' | 'otp' | 'submitting' | 'verifying';

export const PromptLoginWidget: React.FC<PromptLoginWidgetProps> = ({
  data,
  onLoginCompleted,
  onLoginFailed,
  readOnly = false,
}) => {
  const ctx = useOptionalConciergeContext();
  const inlineSignIn = ctx?.inlineSignIn;
  const inlineVerifyOtp = ctx?.inlineVerifyOtp;
  const requestSignIn = ctx?.requestSignIn;

  // Plan 7 C4 — Shopify surface: tappable sign-in card instead of phone/OTP.
  // Double-gated: the server's `loginMethod` is authoritative; the client
  // `isBackRoom()` check guarantees no phone UI on-store even before the
  // surface-aware backend deploys.
  const isShopifyLink = data.loginMethod === 'shopify_link' || isBackRoom();

  const [step, setStep] = useState<LoginStep>(data.stage === 'otp' ? 'otp' : 'phone');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [session, setSession] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  /**
   * Display-only echo of what the model just heard. The widget waits for
   * `data.confirmed === true` (or a Continue click) before doing anything
   * with these digits. `kind`/`value` drive the small "Heard you say…
   * say 'yes' to send, or correct it" caption.
   */
  const [pendingEcho, setPendingEcho] = useState<{ kind: 'phone' | 'otp'; value: string } | null>(null);

  const phoneInputRef = useRef<HTMLInputElement>(null);
  const otpInputRef = useRef<HTMLInputElement>(null);
  /**
   * Track the last (phone, confirmed) and (otp, confirmed) pair we
   * actually acted on so a stale re-emission of the same payload does
   * not double-fire Cognito.
   */
  const lastAppliedRef = useRef<{ phone?: string; otp?: string; phoneConfirmed?: boolean; otpConfirmed?: boolean }>(
    {},
  );

  useEffect(() => {
    if (step === 'phone') phoneInputRef.current?.focus();
    if (step === 'otp') otpInputRef.current?.focus();
  }, [step]);

  // Plan 7 C4 — the promote trigger for the tab-based Shopify login (the
  // D-24-recorded choice: storage-event listener + passive backstop, no
  // polling). The login tab lands back on the storefront, redeems
  // `bcLoginCode`, and Amplify writes the Cognito tokens to localStorage —
  // which is shared same-origin, so this tab gets a `storage` event. On the
  // token write we fire `onLoginCompleted` (same path OTP verification
  // uses): the session hook resolves fresh auth headers itself, POSTs
  // /voice/auth-promote, clears the card, and pins the blocked-tool retry.
  // If the event is ever missed, the backend's passive visitor→consumer
  // promotion on the next /voice/tool call still recovers the session.
  const shopifyLoginFiredRef = useRef(false);
  useEffect(() => {
    if (!isShopifyLink || readOnly) return;
    const onStorage = (e: StorageEvent) => {
      if (shopifyLoginFiredRef.current) return;
      if (!e.key || !e.newValue) return;
      // Amplify key shape: CognitoIdentityServiceProvider.<clientId>.<user>.idToken
      if (e.key.includes('CognitoIdentityServiceProvider') && e.key.endsWith('.idToken')) {
        shopifyLoginFiredRef.current = true;
        onLoginCompleted?.();
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [isShopifyLink, readOnly, onLoginCompleted]);

  // ── Cognito wrappers ──────────────────────────────────────────────────────

  const submitPhone = useCallback(
    async (rawPhone: string) => {
      if (!inlineSignIn) {
        setError('Sign-in unavailable right now.');
        return;
      }
      setError(null);
      setPendingEcho(null);
      setStep('submitting');
      const e164 = parsePhoneNumber(rawPhone, { regionCode: 'US' }).number?.e164 ?? rawPhone;
      const res = await inlineSignIn(e164);
      if ('error' in res) {
        setStep('phone');
        setError(res.error);
        return;
      }
      setSession(res.session);
      setStep('otp');
    },
    [inlineSignIn],
  );

  const verifyOtp = useCallback(
    async (rawOtp: string) => {
      if (!inlineVerifyOtp) {
        setError('Sign-in unavailable right now.');
        onLoginFailed?.('inline verify unavailable');
        return;
      }
      setError(null);
      setPendingEcho(null);
      setStep('verifying');
      const res = await inlineVerifyOtp(session, rawOtp);
      if (res.success) {
        // Let the session hook close out the widget + post /auth-promote.
        onLoginCompleted?.();
      } else {
        setStep('otp');
        setError(res.error);
        // D-48 follow-up: break the model's silence (it was gated by the
        // `tool_choice:'none'` filler response.create sent right after
        // submit_otp_for_login(confirmed:true)). The session hook fires
        // an unconstrained response.create with "ask the user to repeat
        // the code" instructions; widget stays in OTP step locally.
        onLoginFailed?.(res.error || 'verification failed');
      }
    },
    [inlineVerifyOtp, session, onLoginCompleted, onLoginFailed],
  );

  // ── Speech-driven prefill (model emitted submit_*_for_login) ──────────────
  //
  // Two-step contract:
  //  1. Model passes `confirmed: false` (or omits) → we ECHO the digits in
  //     the input + show a small "Heard you say… say 'yes' to send" caption.
  //     We do NOT call Cognito.
  //  2. Model passes `confirmed: true` after the user verbally confirms →
  //     we actually fire `inlineSignIn` / `inlineVerifyOtp`.
  //
  // A corrected re-emission (different digits, confirmed:false) at OTP
  // step rolls the widget back to the phone step so the user can re-OTP
  // for the corrected number — this avoids the "OTP-for-wrong-session"
  // failure mode the live call `36fc1949-...` hit.
  useEffect(() => {
    const incomingPhone = data.prefill?.phone;
    if (!incomingPhone) return;
    const digits = toDigits(incomingPhone).slice(0, 10);
    if (digits.length === 0) return;
    if (readOnly) {
      // Replay mode: display the captured digits, no Cognito calls.
      setPhone(digits);
      return;
    }
    const incomingConfirmed = data.confirmed === true;
    const alreadyApplied =
      lastAppliedRef.current.phone === digits &&
      lastAppliedRef.current.phoneConfirmed === incomingConfirmed;
    if (alreadyApplied) return;

    // Always sync the input field with what we just heard.
    setPhone(digits);

    if (incomingConfirmed) {
      lastAppliedRef.current.phone = digits;
      lastAppliedRef.current.phoneConfirmed = true;
      if (isPhoneValid(digits)) {
        void submitPhone(digits);
      } else {
        setError("That doesn't look like a complete number — please correct it.");
      }
      return;
    }

    // Display-only: roll back to phone step if a corrected phone arrives
    // while we were waiting for OTP (e.g. user just realized the previous
    // number was misheard).
    if (step !== 'phone') {
      setStep('phone');
      setOtp('');
      setSession(null);
      lastAppliedRef.current.otp = undefined;
      lastAppliedRef.current.otpConfirmed = undefined;
    }
    lastAppliedRef.current.phone = digits;
    lastAppliedRef.current.phoneConfirmed = false;
    setPendingEcho({ kind: 'phone', value: digits });
    setError(null);
  }, [data.prefill?.phone, data.confirmed, step, submitPhone]);

  useEffect(() => {
    const incomingOtp = data.prefill?.otp;
    if (!incomingOtp) return;
    const digits = toDigits(incomingOtp).slice(0, 6);
    if (digits.length === 0) return;
    if (readOnly) {
      // Replay mode: display the captured digits, no Cognito calls.
      setOtp(digits);
      return;
    }
    const incomingConfirmed = data.confirmed === true;
    const alreadyApplied =
      lastAppliedRef.current.otp === digits &&
      lastAppliedRef.current.otpConfirmed === incomingConfirmed;
    if (alreadyApplied) return;

    setOtp(digits);

    if (incomingConfirmed) {
      lastAppliedRef.current.otp = digits;
      lastAppliedRef.current.otpConfirmed = true;
      if (digits.length === 6) {
        void verifyOtp(digits);
      } else {
        setError('Only got part of the code — please say the full six digits again.');
      }
      return;
    }

    lastAppliedRef.current.otp = digits;
    lastAppliedRef.current.otpConfirmed = false;
    setPendingEcho({ kind: 'otp', value: digits });
    setError(null);
  }, [data.prefill?.otp, data.confirmed, step, verifyOtp]);

  // Clear the pending-echo caption if the user starts typing over the
  // speech-prefilled value (typing is an implicit cancel of the echo).
  useEffect(() => {
    if (pendingEcho && pendingEcho.kind === 'phone' && pendingEcho.value !== phone) {
      setPendingEcho(null);
    }
  }, [phone, pendingEcho]);
  useEffect(() => {
    if (pendingEcho && pendingEcho.kind === 'otp' && pendingEcho.value !== otp) {
      setPendingEcho(null);
    }
  }, [otp, pendingEcho]);

  // ── UI ────────────────────────────────────────────────────────────────────

  // Plan 7 C4 — Shopify-surface sign-in card. Unlike the phone/OTP card this
  // variant IS tappable: D-77's read-only rule exists because the *voice
  // model* drives the phone flow; here the *shopper's tap* is the flow. The
  // login opens in a NEW TAB (`requestSignIn({ newTab: true })`) so the live
  // WebRTC call and mic permission in this tab survive.
  if (isShopifyLink) {
    const subtitle = data.reason
      ? `Sign in with your store account to ${data.reason}.`
      : 'Sign in with your store account to continue.';
    return (
      <div style={CARD_STYLE} role="dialog" aria-labelledby="voice-login-title">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: readOnly ? 0 : 12 }}>
          <div style={ICON_STYLE} aria-hidden>
            {LockGlyph}
          </div>
          <div>
            <div id="voice-login-title" style={TITLE_STYLE}>
              Sign in to continue
            </div>
            <div style={SUBTITLE_STYLE}>{subtitle}</div>
          </div>
        </div>
        {!readOnly && (
          <>
            <button type="button" onClick={() => requestSignIn?.({ newTab: true })} style={SIGNIN_BUTTON_STYLE}>
              Sign in
            </button>
            <div style={{ ...SUBTITLE_STYLE, marginTop: 8, textAlign: 'center' }}>
              Opens in a new tab — your call stays connected.
            </div>
          </>
        )}
      </div>
    );
  }

  // Replay mode (Plan 7b) doesn't need the Cognito context — it never calls
  // inlineSignIn / inlineVerifyOtp. Skip the unavailability guard so a
  // persisted prompt-login part still renders the captured digits.
  if (!readOnly && (!inlineSignIn || !inlineVerifyOtp)) {
    return (
      <div style={{ ...CARD_STYLE, color: 'var(--voice-widget-warn, #DC2626)' }}>
        Sign-in is unavailable in this environment.
      </div>
    );
  }

  const isPhoneStep = step === 'phone' || step === 'submitting';
  const submitting = step === 'submitting' || step === 'verifying';

  const maskedPhone = applyPhoneMask(phone);
  const subtitle = isPhoneStep
    ? data.reason
      ? `Sign in to ${data.reason}. Just say your phone number — I'll fill it in for you.`
      : "Just say your phone number — I'll fill it in for you."
    : `We sent a 6-digit code to ${maskedPhone}. Say the digits aloud to continue.`;

  return (
    <div style={CARD_STYLE} role="dialog" aria-labelledby="voice-login-title">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <div style={ICON_STYLE} aria-hidden>
          {isPhoneStep ? PhoneGlyph : LockGlyph}
        </div>
        <div>
          <div id="voice-login-title" style={TITLE_STYLE}>
            {isPhoneStep ? 'Sign in to continue' : "Confirm it's you"}
          </div>
          <div style={SUBTITLE_STYLE}>{subtitle}</div>
        </div>
      </div>

      {/*
        D-77 (live + replay) — input is ALWAYS read-only and the widget
        renders NO buttons. The widget is a pure display surface; the
        voice realtime model is the sole driver. Digits arrive via the
        speech-prefill effect above (`submit_phone_for_login` /
        `submit_otp_for_login`); confirmation happens through the
        two-call (display → verbal yes → commit) contract — never via a
        click or keystroke. This eliminates the "user edited mid-flow"
        and "user clicked Continue out of sync with the model" failure
        modes that made D-48 incidents hard to debug.
      */}
      <label style={LABEL_STYLE}>
        {isPhoneStep ? 'Phone number' : 'Verification code'}
        <input
          type={isPhoneStep ? 'tel' : 'text'}
          inputMode="none"
          maxLength={isPhoneStep ? 14 : 6}
          ref={isPhoneStep ? phoneInputRef : otpInputRef}
          value={isPhoneStep ? maskedPhone : otp}
          disabled={submitting}
          readOnly
          aria-readonly
          tabIndex={-1}
          placeholder={isPhoneStep ? '(555) 555-5555' : '123456'}
          style={INPUT_STYLE}
        />
      </label>

      {!readOnly && pendingEcho && (
        <div style={CAPTION_STYLE}>
          <span>
            Heard you say{' '}
            <strong style={{ color: 'var(--voice-widget-fg, #111827)' }}>
              {pendingEcho.kind === 'phone' ? applyPhoneMask(pendingEcho.value) : pendingEcho.value}
            </strong>
            . Say <strong>yes</strong> to {pendingEcho.kind === 'phone' ? 'send the code' : 'verify'}, or correct it.
          </span>
        </div>
      )}

      {!readOnly && error && <div style={ERROR_STYLE}>{error}</div>}

      {!readOnly && submitting && (
        <div style={STATUS_STYLE}>{isPhoneStep ? 'Sending code…' : 'Verifying…'}</div>
      )}
    </div>
  );
};

export default PromptLoginWidget;

// ─── Inline SVG glyphs (avoid adding an icon dep) ─────────────────────────────

const PhoneGlyph = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2z" />
  </svg>
);

const LockGlyph = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

// ─── Inline style bag (kept local — matches sibling-widget convention) ────────

const CARD_STYLE: React.CSSProperties = {
  width: '100%',
  maxWidth: 380,
  background: 'var(--voice-widget-card-bg, #FFFFFF)',
  border: '1px solid var(--voice-widget-border, #E5E7EB)',
  borderRadius: 16,
  padding: '18px 18px 16px',
  boxSizing: 'border-box',
  boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
};

const ICON_STYLE: React.CSSProperties = {
  width: 34,
  height: 34,
  borderRadius: '50%',
  background: 'var(--voice-widget-img-bg, #F9FAFB)',
  color: 'var(--voice-widget-accent-dark, #0D9488)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

const TITLE_STYLE: React.CSSProperties = {
  fontSize: 14,
  fontWeight: 700,
  color: 'var(--voice-widget-fg, #111827)',
  lineHeight: 1.3,
};

const SUBTITLE_STYLE: React.CSSProperties = {
  fontSize: 12,
  color: 'var(--voice-widget-muted, #6B7280)',
  marginTop: 3,
  lineHeight: 1.4,
};

const LABEL_STYLE: React.CSSProperties = {
  display: 'block',
  fontSize: 11,
  fontWeight: 600,
  color: 'var(--voice-widget-muted, #6B7280)',
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  marginBottom: 12,
};

const INPUT_STYLE: React.CSSProperties = {
  display: 'block',
  marginTop: 6,
  width: '100%',
  boxSizing: 'border-box',
  padding: '12px 14px',
  fontSize: 16,
  fontWeight: 500,
  color: 'var(--voice-widget-fg, #111827)',
  background: 'var(--voice-widget-img-bg, #F9FAFB)',
  border: '1px solid var(--voice-widget-border, #E5E7EB)',
  borderRadius: 10,
  outline: 'none',
  fontVariantNumeric: 'tabular-nums',
  letterSpacing: 1,
};

const CAPTION_STYLE: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
  fontSize: 11,
  color: 'var(--voice-widget-muted, #6B7280)',
  background: 'var(--voice-widget-img-bg, #F9FAFB)',
  border: '1px dashed var(--voice-widget-border, #E5E7EB)',
  borderRadius: 8,
  padding: '6px 10px',
  marginBottom: 10,
};

const ERROR_STYLE: React.CSSProperties = {
  fontSize: 11,
  color: 'var(--voice-widget-warn, #DC2626)',
  background: 'var(--voice-widget-warn-bg, #FEF2F2)',
  border: '1px solid var(--voice-widget-warn-border, #FECACA)',
  borderRadius: 6,
  padding: '6px 10px',
  marginBottom: 10,
};

const STATUS_STYLE: React.CSSProperties = {
  width: '100%',
  textAlign: 'center',
  padding: '11px 14px',
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--voice-widget-muted, #6B7280)',
};

// Plan 7 C4 — the tappable sign-in button on the shopify_link card.
const SIGNIN_BUTTON_STYLE: React.CSSProperties = {
  display: 'block',
  width: '100%',
  boxSizing: 'border-box',
  padding: '12px 14px',
  fontSize: 14,
  fontWeight: 600,
  color: 'var(--voice-widget-card-bg, #FFFFFF)',
  background: 'var(--voice-widget-accent-dark, #0D9488)',
  border: 'none',
  borderRadius: 10,
  cursor: 'pointer',
};
